/**
 * 業務フロー図の配置 — レーン帯 × 列の格子。
 *
 * 自動レイアウトのライブラリは足さない。業務フローはレーン（縦 ＝ アクター）と遷移の並び（横）の
 * 2 軸が初めから決まっている。力学モデルや階層レイアウトを持ち込むと、同じフローを開くたびに
 * 配置が変わって「前に見た図」と比べられなくなる。
 *
 * 横位置は遷移の最長路の深さで決める。開始から数えて何手目かが列になるので、分岐した枝が合流する
 * とき合流先が必ず右側へ来る。
 *
 * **札（出口へ・行き先の無い止まる）も格子に載せる。** 札は終了の節点と同じく流れの行き止まりで、
 * 元の節点の次の列に置く。格子の外（節点の右下）に浮かせると、ほかの線の水路が札の上を走り、
 * 札へ向かう線も水路の割り当てから漏れて重なる。
 *
 * 描画ではなく座標だけをここに閉じるのは、jsdom で React Flow の実描画を検証するのが難しく、配置の
 * 誤りがテストの外に落ちるためである。純関数にして境界を直接検証する。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの節点と札を、レーン帯 × 遷移の深さの格子に置き、列ごとの寸法と図の寸法を決める。
 * @graph-connects none
 */
import type { FlowNodeView, FlowTransitionView, FlowView } from '@cortex/domain-spec-console';

import { INITIAL_MARGIN } from '../flow-workspace/flow-scale.js';
import {
  CANVAS_PADDING_X,
  CONTROL_CELL_WIDTH,
  GUTTER_WITH_LABEL,
  GUTTER_WITHOUT_LABEL,
  LANE_HEIGHT,
  LANE_LABEL_WIDTH,
  NODE_OFFSET_Y,
  NODE_WIDTH,
} from './constants.js';

/**
 * どのレーンにも寄せられない節点を載せる帯の ID。
 *
 * レーンは業務のアクターなので、アクターを引けない業務の節点はレーンを持たない。**捨てると図から
 * 節点が消え、矢印だけが宙に浮く**ので、専用の帯へ載せて必ず出す。実在しうるアクターの id
 * （正の整数）と衝突しない値にしてある。
 * @graph-connects none
 */
export const UNASSIGNED_LANE_ID = 0;

/** 図に置くものの形。節点 5 種と札 2 種。 @graph-connects none */
export type DiagramShape = FlowNodeView['kind'] | 'exitTag' | 'stopTag';

/** 図に置くもの 1 個（節点か札）。 */
export interface DiagramNode {
  /** React Flow の ID。節点は局所 ID の文字列、札は `tag:<枝の ID>` */
  key: string;
  /** 形 */
  shape: DiagramShape;
  /** 節点なら業務フローの節点。札なら null */
  flowNode: FlowNodeView | null;
  /** 札ならそこへ向かう枝の ID。節点なら null */
  transitionId: string | null;
}

/** 図の上の線 1 本。 */
export interface DiagramEdge {
  /** 元の枝 */
  transition: FlowTransitionView;
  /** 始点の `DiagramNode.key` */
  from: string;
  /** 終点の `DiagramNode.key`（札へ向かう枝は札の key） */
  to: string;
}

/** 図に置くものと線。 */
export interface DiagramGraph {
  /** 節点と札 */
  nodes: DiagramNode[];
  /** 描ける線 */
  edges: DiagramEdge[];
}

/**
 * 節点ではなく札へ向かう枝か（出口へ、または行き先の無い止まる）。
 *
 * @param transition 枝
 * @returns 札へ向かうなら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの枝が、節点ではなく出口・止まるの札へ向かうかを判定する。
 * @graph-connects none
 */
export function isTagTransition(transition: FlowTransitionView): boolean {
  if (transition.destinationKind === 'exit') return true;
  return transition.destinationKind === 'stop' && transition.to === null;
}

/**
 * 業務フローを、図に置くもの（節点と札）と線に直す。
 *
 * **描けない枝は落とす。** 始点が図に無い枝、行き先の節点が図に無い枝、自分へ戻る枝は React Flow が
 * 描けない。骨格の書き間違いは違反として別に出るので、ここでは描けるものだけを渡す。
 *
 * @param flow 業務フロー
 * @returns 節点・札と線
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの節点と、出口・止まるの枝ごとの札を図に置くものとして並べ、描ける枝を線にする。
 * @graph-connects isTagTransition [calls] 札へ向かう枝かの判定
 */
export function diagramGraphOf(flow: FlowView): DiagramGraph {
  const nodeIds = new Set(flow.nodes.map((node) => node.nodeId));
  const nodes: DiagramNode[] = flow.nodes.map((node) => ({
    key: String(node.nodeId),
    shape: node.kind,
    flowNode: node,
    transitionId: null,
  }));
  const edges: DiagramEdge[] = [];

  for (const transition of flow.transitions) {
    if (!nodeIds.has(transition.from)) continue;
    if (isTagTransition(transition)) {
      const key = `tag:${transition.id}`;
      nodes.push({
        key,
        shape: transition.destinationKind === 'exit' ? 'exitTag' : 'stopTag',
        flowNode: null,
        transitionId: transition.id,
      });
      edges.push({ transition, from: String(transition.from), to: key });
      continue;
    }
    if (transition.to === null || transition.to === transition.from) continue;
    if (!nodeIds.has(transition.to)) continue;
    edges.push({ transition, from: String(transition.from), to: String(transition.to) });
  }

  return { nodes, edges };
}

/**
 * 数え上げ Map の読み出し。未登録の鍵は既定値とみなす。
 *
 * 列の深さ・入次数・段数・帯の上端はどれも「まだ数えていないなら 0」で意味が通る。既定値の決定を
 * この 1 箇所に閉じる。
 *
 * @param counts 数え上げ Map
 * @param key 読む鍵
 * @param fallback 未登録のときの値
 * @returns 読んだ値、または既定値
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 数え上げ Map の値を、未登録なら既定値として読み出す。
 * @graph-connects none
 */
export function countOf<K>(counts: ReadonlyMap<K, number>, key: K, fallback = 0): number {
  return counts.get(key) ?? fallback;
}

/**
 * 位相順序の 1 段を進め、次に深さの確定するものを返す。
 *
 * 各々の深さは「先行の深さ + 1」の最大値になる。入次数が 0 に落ちた時点で、先行はすべて処理済み
 * なので深さが確定する。
 *
 * @param frontier いま深さの確定したもの
 * @param outgoing key → 前へ進む線の行き先
 * @param indegree key → まだ処理していない入ってくる線の数（書き換える）
 * @param depth key → 深さ（書き換える）
 * @returns 次に深さの確定するもの
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の位相順序を 1 段進め、深さの確定した節点・札を返す。
 * @graph-connects countOf [calls] 数え上げ Map の読み出し
 */
function relaxFrontier(
  frontier: readonly string[],
  outgoing: ReadonlyMap<string, readonly string[]>,
  indegree: Map<string, number>,
  depth: Map<string, number>,
): string[] {
  const next: string[] = [];
  for (const current of frontier) {
    const currentDepth = countOf(depth, current);
    for (const target of outgoing.get(current) ?? []) {
      depth.set(target, Math.max(countOf(depth, target), currentDepth + 1));
      const remaining = countOf(indegree, target) - 1;
      indegree.set(target, remaining);
      if (remaining === 0) next.push(target);
    }
  }
  return next;
}

/**
 * 節点ごとの列（遷移の最長路の深さ）を決める。
 *
 * **戻る枝は外す。** 戻る枝は前の節点へ向かうので、含めると循環になり列がずれる。循環に含まれて
 * 位相順序で解けなかった節点は、宣言順に「解決済みの先行の最大 + 1」を与える。書き途中の業務
 * フローは循環しうるので、解けない節点を落とすと図から消え、書いた本人が「保存できていない」と
 * 誤解する。
 *
 * @param graph 図に置くものと線
 * @returns key → 列位置
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの節点と札の列位置を、前へ進む線の最長路の深さとして決める。
 * @graph-connects relaxFrontier [calls] 位相順序を 1 段進める
 * @graph-connects countOf [calls] 数え上げ Map の読み出し
 */
export function computeColumns(graph: DiagramGraph): Map<string, number> {
  const edges = graph.edges.filter((edge) => edge.transition.destinationKind !== 'back');

  const outgoing = new Map<string, string[]>();
  const indegree = new Map<string, number>();
  for (const edge of edges) {
    const targets = outgoing.get(edge.from);
    if (targets === undefined) outgoing.set(edge.from, [edge.to]);
    else targets.push(edge.to);
    indegree.set(edge.to, countOf(indegree, edge.to) + 1);
  }

  const depth = new Map<string, number>(graph.nodes.map((node) => [node.key, 0]));
  const settled = new Set<string>();
  let frontier = graph.nodes
    .filter((node) => countOf(indegree, node.key) === 0)
    .map((node) => node.key);

  while (frontier.length > 0) {
    for (const current of frontier) settled.add(current);
    frontier = relaxFrontier(frontier, outgoing, indegree, depth);
  }

  for (const node of graph.nodes) {
    if (settled.has(node.key)) continue;
    const base = edges
      .filter((edge) => edge.to === node.key && settled.has(edge.from))
      .reduce((max, edge) => Math.max(max, countOf(depth, edge.from)), -1);
    depth.set(node.key, base + 1);
    settled.add(node.key);
  }

  return depth;
}

/**
 * 節点と札を載せる帯を決める。
 *
 * 業務の節点は自分のアクターの帯に載る。アクターを引けない業務の節点は書き手への残 TODO なので、
 * 寄せずに `UNASSIGNED_LANE_ID` の帯へ出す。
 *
 * 端の節点・菱形・札はレーンを持てない。持てないからといって専用の帯へ寄せると、**1 本のフローが
 * 「アクターの帯」と「アクターでない帯」に分断され、矢印が帯をまたぐたびに読み手の目が縦へ飛ぶ**。
 * 流れの上で最も近い業務の帯へ寄せる。**直前を先に見て、無ければ直後を見る。** 終了や札は直前の
 * 業務をしたアクターの到達点で、開始は直前が無いので直後を見るしかない。距離が同じ候補が複数ある
 * ときは宣言順で先のものを採る（同じ骨格からは常に同じ図が出る必要がある）。
 *
 * @param graph 図に置くものと線
 * @returns key → 帯の ID
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの節点と札を載せる帯を、業務はアクターから、それ以外は流れの上で最も近い業務から決める。
 * @graph-connects none
 */
export function resolveLaneBands(graph: DiagramGraph): Map<string, number> {
  const laneOf = new Map(
    graph.nodes.map((node) => [node.key, node.flowNode?.actor?.id ?? null] as const),
  );
  const upstream = new Map<string, string[]>();
  const downstream = new Map<string, string[]>();
  const link = (map: Map<string, string[]>, from: string, to: string): void => {
    const bucket = map.get(from);
    if (bucket === undefined) map.set(from, [to]);
    else bucket.push(to);
  };
  for (const edge of graph.edges) {
    link(downstream, edge.from, edge.to);
    link(upstream, edge.to, edge.from);
  }

  const nearest = (start: string, neighbors: ReadonlyMap<string, string[]>): number | null => {
    const seen = new Set<string>([start]);
    const queue = [...(neighbors.get(start) ?? [])];
    while (queue.length > 0) {
      const current = queue.shift();
      if (current === undefined || seen.has(current)) continue;
      seen.add(current);
      const lane = laneOf.get(current) ?? null;
      if (lane !== null) return lane;
      queue.push(...(neighbors.get(current) ?? []));
    }
    return null;
  };

  const bands = new Map<string, number>();
  for (const node of graph.nodes) {
    const own = laneOf.get(node.key) ?? null;
    if (own !== null) {
      bands.set(node.key, own);
      continue;
    }
    if (node.shape === 'business') {
      bands.set(node.key, UNASSIGNED_LANE_ID);
      continue;
    }
    bands.set(
      node.key,
      nearest(node.key, upstream) ?? nearest(node.key, downstream) ?? UNASSIGNED_LANE_ID,
    );
  }
  return bands;
}

/**
 * レーンごとに要る段数を数える。
 *
 * 同じレーンの同じ列に 2 個以上が載ることがある。段を分けずに置くと 2 個が**完全に重なり**、後ろの
 * 節点が前の節点に隠れて図から消える。
 *
 * @param graph 図に置くものと線
 * @param columns key ごとの列位置
 * @param bands key ごとの帯
 * @returns 帯の ID → 要る段数
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーンごとに、同じ列へ何個の節点・札が載るかを数える。
 * @graph-connects countOf [calls] 数え上げ Map の読み出し
 */
export function computeSlotCounts(
  graph: DiagramGraph,
  columns: ReadonlyMap<string, number>,
  bands: ReadonlyMap<string, number>,
): Map<number, number> {
  const perLane = new Map<number, number>();
  const perCell = new Map<string, number>();
  for (const node of graph.nodes) {
    const laneId = countOf(bands, node.key, UNASSIGNED_LANE_ID);
    const cell = `${String(laneId)}:${String(countOf(columns, node.key))}`;
    const used = countOf(perCell, cell) + 1;
    perCell.set(cell, used);
    perLane.set(laneId, Math.max(countOf(perLane, laneId), used));
  }
  return perLane;
}

/** レーン帯 1 本。 */
export interface LaneRow {
  /** アクターの id。寄せ先が無い帯は `UNASSIGNED_LANE_ID` */
  laneId: number;
  /** レーンの表示名。寄せ先が無い帯では空文字 */
  label: string;
  /** 選んだ言語に訳が入っていないか */
  untranslated: boolean;
  /** 帯の上端 Y 座標 */
  y: number;
  /** 帯の高さ。同じ列に複数並ぶレーンは段数ぶん高くなる */
  height: number;
}

/** 読取が宣言したレーン 1 本（座標を持つ前）。 @graph-connects none */
type DeclaredLane = Omit<LaneRow, 'y' | 'height'>;

/**
 * 読取が返したレーンを、帯の並びの材料に直す。
 *
 * @param flow 業務フロー
 * @returns 宣言順のレーン
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 読取が返したレーンを、帯の並びを決める材料（アクターの id・名前・訳の有無）に直す。
 * @graph-connects none
 */
function declaredLanesOf(flow: FlowView): DeclaredLane[] {
  return flow.lanes.map((lane) => ({
    laneId: lane.actorId,
    label: lane.label,
    untranslated: lane.untranslated,
  }));
}

/**
 * 宣言されたレーン → それに載らない帯の初出順で並べ、段数ぶんの高さと座標を与える。
 *
 * 読取の `lanes` に載らない帯（アクターを引けない業務の帯）は、**捨てるとその節点が図から消える**
 * ので後ろへ足して必ず出す。上端は前の帯の高さを積み上げて求めるので、段の多いレーンがあっても
 * 下の帯がその中へ食い込まない。
 *
 * @param declared 宣言順のレーン
 * @param bandIds 載る帯の ID（置くものの順）
 * @param slotCounts 帯ごとの段数
 * @returns 上から順のレーン帯
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 宣言されたレーンと節点が載る帯から、段数ぶんの高さと座標を持つレーン帯を並べる。
 * @graph-connects countOf [calls] 数え上げ Map の読み出し
 */
function laneRowsOf(
  declared: readonly DeclaredLane[],
  bandIds: readonly number[],
  slotCounts: ReadonlyMap<number, number>,
): LaneRow[] {
  const declaredIds = new Set(declared.map((lane) => lane.laneId));
  const extra: number[] = [];
  for (const laneId of bandIds) {
    if (declaredIds.has(laneId) || extra.includes(laneId)) continue;
    extra.push(laneId);
  }

  let top = 0;
  return [...declared, ...extra.map((laneId) => ({ laneId, label: '', untranslated: false }))].map(
    (lane) => {
      const height = Math.max(1, countOf(slotCounts, lane.laneId)) * LANE_HEIGHT;
      const row = { ...lane, y: top, height };
      top += height;
      return row;
    },
  );
}

/** 列 1 本の寸法。列ごとに中身が違うので、幅は一定ではない。 */
export interface ColumnMetrics {
  /** 升目の左端 X */
  left: number;
  /** 升目の幅。業務の節点が無い列は狭い */
  cellWidth: number;
  /** 升目の右のガターの幅。ラベルが入らない列は狭い */
  gutterWidth: number;
}

/**
 * 列ごとの寸法を決める。
 *
 * 幅は列の中身から決まる。①その列に業務の節点があるか（無ければ升目は端の節点ぶんで足りる）
 * ②その列から出る線にラベルがあるか（無ければガターにラベルの帯は要らない）。戻る線も結末の行が
 * 空ならラベルを持たない（「戻る」の語は線を指したときに出す）。
 *
 * @param graph 図に置くものと線
 * @param columns key ごとの列位置
 * @returns 左から順の列の寸法
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 列ごとの升目幅とガター幅を、その列の中身（業務の節点の有無・線のラベルの有無）から決める。
 * @graph-connects countOf [calls] 数え上げ Map の読み出し
 */
export function computeColumnMetrics(
  graph: DiagramGraph,
  columns: ReadonlyMap<string, number>,
): ColumnMetrics[] {
  const total = graph.nodes.reduce((max, node) => Math.max(max, countOf(columns, node.key) + 1), 0);
  const hasBusiness = new Set<number>();
  for (const node of graph.nodes) {
    if (node.shape === 'business') hasBusiness.add(countOf(columns, node.key));
  }
  const hasLabel = new Set<number>();
  for (const edge of graph.edges) {
    if (edge.transition.label === '') continue;
    hasLabel.add(countOf(columns, edge.from));
  }

  const metrics: ColumnMetrics[] = [];
  let left = LANE_LABEL_WIDTH;
  for (let column = 0; column < total; column += 1) {
    const cellWidth = hasBusiness.has(column) ? NODE_WIDTH : CONTROL_CELL_WIDTH;
    const gutterWidth = hasLabel.has(column) ? GUTTER_WITH_LABEL : GUTTER_WITHOUT_LABEL;
    metrics.push({ left, cellWidth, gutterWidth });
    left += cellWidth + gutterWidth;
  }
  return metrics;
}

/** 配置済みのもの 1 個（節点か札）。 */
export interface PlacedDiagramNode {
  /** 置いたもの */
  item: DiagramNode;
  /** 升目の左端 X 座標 */
  x: number;
  /** 上端 Y 座標 */
  y: number;
  /** 升目の幅 */
  cellWidth: number;
  /** 升目の右のガターの幅 */
  gutterWidth: number;
}

/** 業務フロー図 1 枚分の配置。 */
export interface BusinessFlowLayout {
  /** 図に置くものと線 */
  graph: DiagramGraph;
  /** 上から順のレーン帯 */
  lanes: LaneRow[];
  /** 左から順の列の寸法 */
  columns: ColumnMetrics[];
  /** 配置済みの節点と札 */
  nodes: PlacedDiagramNode[];
  /** 図全体の幅 */
  width: number;
  /** 図全体の高さ */
  height: number;
}

/** 1 枚の骨格から決まる格子（列・帯・段数）。 @graph-connects none */
interface Grid {
  /** 図に置くものと線 */
  graph: DiagramGraph;
  /** key ごとの列位置 */
  columns: Map<string, number>;
  /** key ごとの帯 */
  bands: Map<string, number>;
  /** 帯ごとの段数 */
  slotCounts: Map<number, number>;
}

/**
 * 1 枚の骨格から、置くものの列・帯・帯ごとの段数を決める。
 *
 * @param flow 業務フロー
 * @returns 列・帯・段数
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー 1 枚の節点と札の列・載せる帯・帯ごとの段数を決める。
 * @graph-connects diagramGraphOf [calls] 節点・札と線への変換
 * @graph-connects computeColumns [calls] 列位置の決定
 * @graph-connects resolveLaneBands [calls] 載せる帯の決定
 * @graph-connects computeSlotCounts [calls] 帯ごとの段数
 */
function gridOf(flow: FlowView): Grid {
  const graph = diagramGraphOf(flow);
  const columns = computeColumns(graph);
  const bands = resolveLaneBands(graph);
  return { graph, columns, bands, slotCounts: computeSlotCounts(graph, columns, bands) };
}

/**
 * 与えられたレーン帯の上へ、1 枚の骨格の節点と札を置く。
 *
 * レーン帯を引数で受け取るのは、変更前と変更後の 2 枚を同じ帯で並べるためである。
 *
 * @param grid その骨格の列・帯・段数
 * @param laneRows 置く先のレーン帯
 * @returns 配置
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 与えられたレーン帯の上へ、業務フローの節点と札を列と段に沿って置き、図の寸法を返す。
 * @graph-connects computeColumnMetrics [calls] 列ごとの寸法決定
 */
function placeOnLanes(grid: Grid, laneRows: LaneRow[]): BusinessFlowLayout {
  const { graph, columns, bands } = grid;
  const metrics = computeColumnMetrics(graph, columns);
  const laneTop = new Map(laneRows.map((lane) => [lane.laneId, lane.y]));
  const usedSlots = new Map<string, number>();

  const placed = graph.nodes.map((item): PlacedDiagramNode => {
    const column = countOf(columns, item.key);
    const laneId = countOf(bands, item.key, UNASSIGNED_LANE_ID);
    /* 同じ升目（レーン × 列）に載る 2 個目以降は段を下げる。重ねると後ろが隠れる。 */
    const cell = `${String(laneId)}:${String(column)}`;
    const slot = countOf(usedSlots, cell);
    usedSlots.set(cell, slot + 1);
    return {
      item,
      x: metrics[column]?.left ?? LANE_LABEL_WIDTH,
      y: countOf(laneTop, laneId) + slot * LANE_HEIGHT + NODE_OFFSET_Y,
      cellWidth: metrics[column]?.cellWidth ?? NODE_WIDTH,
      gutterWidth: metrics[column]?.gutterWidth ?? GUTTER_WITH_LABEL,
    };
  });

  const last = metrics[metrics.length - 1];
  return {
    graph,
    lanes: laneRows,
    columns: metrics,
    nodes: placed,
    width:
      last === undefined
        ? LANE_LABEL_WIDTH + CANVAS_PADDING_X
        : last.left + last.cellWidth + last.gutterWidth + CANVAS_PADDING_X,
    height: laneRows.reduce((total, lane) => total + lane.height, 0),
  };
}

/**
 * 業務フロー図 1 枚分のレーン帯と、節点・札の座標を計算する。
 *
 * @param flow 業務フロー
 * @returns 配置
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローのレーン帯と節点・札の座標を、レーン帯 × 遷移の深さの格子として計算する。
 * @graph-connects gridOf [calls] 列・帯・段数の決定
 * @graph-connects laneRowsOf [calls] レーン帯の並び決定
 * @graph-connects placeOnLanes [calls] レーン帯の上への配置
 */
export function computeBusinessFlowLayout(flow: FlowView): BusinessFlowLayout {
  const grid = gridOf(flow);
  return placeOnLanes(
    grid,
    laneRowsOf(declaredLanesOf(flow), [...grid.bands.values()], grid.slotCounts),
  );
}

/** 並べて比べる 2 枚の配置。どちらも同じレーン帯と同じ幅を持つ。 */
export interface ComparedBusinessFlowLayouts {
  /** 変更前の配置 */
  before: BusinessFlowLayout;
  /** 変更後の配置 */
  after: BusinessFlowLayout;
}

/**
 * 変更前と変更後を並べて比べるための配置を、同じレーン帯・同じ幅で 2 枚まとめて計算する。
 *
 * **レーン帯・段数・幅を 2 枚で揃える。** 各枚が自分の節点だけから帯を組むと、片方にしか居ない
 * アクターがあったときに同じアクターの帯が左右で違う高さに出る。幅を揃えないと、狭いほうの帯が
 * 途中で切れる。並びは変更後を先に置き、変更前にしか居ないものを末尾へ回す（読み手がこれから
 * 運用するのは変更後）。
 *
 * @param before 変更前の骨格
 * @param after 変更後の骨格
 * @returns 同じレーン帯・同じ幅を持つ 2 枚の配置
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更前と変更後の業務フロー図の配置を、同じレーン帯・同じ段数・同じ幅で 2 枚まとめて計算する。
 * @graph-connects gridOf [calls] 1 枚ごとの列・帯・段数
 * @graph-connects laneRowsOf [calls] 2 枚で共有するレーン帯
 * @graph-connects placeOnLanes [calls] 共有するレーン帯の上への配置
 */
export function computeComparedBusinessFlowLayouts(
  before: FlowView,
  after: FlowView,
): ComparedBusinessFlowLayouts {
  const beforeGrid = gridOf(before);
  const afterGrid = gridOf(after);

  const declared = declaredLanesOf(after);
  const declaredIds = new Set(declared.map((lane) => lane.laneId));
  for (const lane of declaredLanesOf(before)) {
    if (declaredIds.has(lane.laneId)) continue;
    declaredIds.add(lane.laneId);
    declared.push(lane);
  }
  const slotCounts = new Map(afterGrid.slotCounts);
  for (const [laneId, count] of beforeGrid.slotCounts) {
    slotCounts.set(laneId, Math.max(countOf(slotCounts, laneId), count));
  }
  const laneRows = laneRowsOf(
    declared,
    [...afterGrid.bands.values(), ...beforeGrid.bands.values()],
    slotCounts,
  );

  const beforePlaced = placeOnLanes(beforeGrid, laneRows);
  const afterPlaced = placeOnLanes(afterGrid, laneRows);
  const width = Math.max(beforePlaced.width, afterPlaced.width);
  return { before: { ...beforePlaced, width }, after: { ...afterPlaced, width } };
}

/** 視点（React Flow の viewport）。 */
export interface DiagramViewport {
  /** 横の平行移動 */
  x: number;
  /** 縦の平行移動 */
  y: number;
  /** 倍率 */
  zoom: number;
}

/**
 * 左端の X 座標が `x` の節点を、枠の横の中央に置く視点（実寸）。
 *
 * **図の左端より右へは動かさない。** フローの先頭付近の節点を中央へ置こうとすると図の左に空白が
 * 生まれ、「ここから左にまだ何かある」と読める余白ができる。
 *
 * @param x 寄せる節点の左端の X 座標
 * @param paneWidth 図の器の表示幅（実寸に割り戻した値）
 * @returns 節点を枠の中央に置く視点
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 指定した節点が業務フロー図の枠の横の中央に来る視点を決める。
 * @graph-connects none
 */
export function viewportCenteredOn(x: number, paneWidth: number): DiagramViewport {
  const centered = paneWidth / 2 - (x + NODE_WIDTH / 2);
  return { x: Math.min(INITIAL_MARGIN, centered), y: INITIAL_MARGIN, zoom: 1 };
}

/**
 * 初期表示の視点。実寸のまま、**最初の業務の節点が枠に入る位置**へ合わせる。
 *
 * 単純に左上へ合わせると、開始の節点だけで枠の幅を使い切る図がある。読み手が最初に知りたいのは
 * 「何をする業務が並んでいるか」なので、業務が入るところから見せる。**開く節点を指されているときは、
 * そちらへ枠の中央で寄せる。**
 *
 * @param placed 配置済みの節点と札
 * @param paneWidth 図の器の表示幅
 * @param openNodeId 開いた状態で出す節点の局所 ID。無ければ最初の業務の節点へ寄せる
 * @returns 初期表示の視点
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図の初期表示の視点を、開く節点（無ければ最初の業務の節点）が枠に入る位置に決める。
 * @graph-connects viewportCenteredOn [calls] 節点を枠の中央に置く視点
 */
export function initialViewportOf(
  placed: readonly PlacedDiagramNode[],
  paneWidth: number,
  openNodeId: number | null,
): DiagramViewport {
  const opened =
    openNodeId === null
      ? undefined
      : placed.find((entry) => entry.item.flowNode?.nodeId === openNodeId);
  if (opened !== undefined) return viewportCenteredOn(opened.x, paneWidth);

  const firstBusiness = placed
    .filter((entry) => entry.item.shape === 'business')
    .reduce<number | null>((min, entry) => (min === null ? entry.x : Math.min(min, entry.x)), null);
  if (firstBusiness === null || paneWidth <= 0) {
    return { x: INITIAL_MARGIN, y: INITIAL_MARGIN, zoom: 1 };
  }
  const rightFitted = paneWidth - INITIAL_MARGIN - firstBusiness - NODE_WIDTH;
  return { x: Math.min(INITIAL_MARGIN, rightFitted), y: INITIAL_MARGIN, zoom: 1 };
}

/**
 * 並べる面のうち、いちばん左にある「変わった節点」の X 座標。変わった節点が無ければ null。
 *
 * **案件モードで開いたときは、変わったところから見せる。** 変わった節点が枠の外にあると、開いた
 * 時点では何も変わっていない図に見える。左右 2 面は視点を揃えるので、2 面を通していちばん左の
 * 1 つに寄せる。
 *
 * @param panes 並べる面ごとの配置済みの節点と、節点に載せる差分の状態
 * @returns いちばん左の変わった節点の X 座標。無ければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードで並べる業務フロー図の面のうち、いちばん左にある変わった節点の位置を探す。
 * @graph-connects none
 */
export function firstChangedX(
  panes: readonly {
    placed: readonly PlacedDiagramNode[];
    stateByNodeId: ReadonlyMap<number, unknown>;
  }[],
): number | null {
  return panes
    .flatMap(({ placed, stateByNodeId }) =>
      placed.filter(
        (entry) => entry.item.flowNode !== null && stateByNodeId.has(entry.item.flowNode.nodeId),
      ),
    )
    .reduce<number | null>((min, entry) => (min === null ? entry.x : Math.min(min, entry.x)), null);
}
